import * as Yup from "yup";

export const userProfileSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("First name is required")
    .min(1, "First name must be at least 1 character")
    .max(100, "First name must not exceed 100 characters"),

  lastName: Yup.string()
    .trim()
    .required("Last name is required")
    .min(1, "Last name must be at least 1 character")
    .max(100, "Last name must not exceed 100 characters"),

  ageGroup: Yup.string().required("Age group is required"),
  email: Yup.string()
    .required("Email is required")
    .max(100, "Email must not exceed 100 characters")
    .test(
      "is-valid-email",
      "Invalid email address",
      (value) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ),
  currentCountry: Yup.number().required("Country is required"),
  familyMemberIds: Yup.array()
    .min(1, "At least one option must be selected")
    .required("Family members info is required"),
  passportHold: Yup.array()
    .min(1, "At least one option must be selected")
    .required("Passport information is required"),
  preferredLocationIds: Yup.array()
    .min(1, "At least one property preference must be selected")
    .required("Property preferences are required"),
  noPropertiesLikeToOwn: Yup.string().required("Property count is required"),
  healthPriority: Yup.string().required("Health care preference is required"),
  dayToDay: Yup.array()
    .min(1, "At least one activity must be selected")
    .required("Activities are required"),
  taxFreeCountry: Yup.string().required("Tax preference is required"),
  countriesLikeToLiveIds: Yup.array()
    .min(1, "At least one country preference must be selected")
    .required("Country preferences are required"),
  visaFreeCountry: Yup.string().required("Visa preference is required"),
  interestedCitizenshipId: Yup.number().nullable(),
  travelDistanceHoliday: Yup.string().required("Holiday preference is required"),
  travelDistanceLovedOnes: Yup.string().required(
    "Loved ones distance preference is required"
  ),
  minBudget: Yup.number()
    .transform((value, originalValue) =>
      typeof originalValue === "string" && originalValue.trim() === ""
        ? undefined
        : value
    )
    .required("Minimum budget is required"),
  maxBudget: Yup.number()
    .transform((value, originalValue) =>
      typeof originalValue === "string" && originalValue.trim() === ""
        ? undefined
        : value
    )
    .required("Maximum budget is required"),
  currentPassword: Yup.string().test({
    name: "conditional-validation",
    exclusive: false,
    params: {},
    message: "Old Password is required",
    test: function (value) {
      const { password, confirmPassword } = this.parent;
      // If any of the fields are filled, currentPassword must be required
      return !value && !password && !confirmPassword ? true : !!value;
    },
  }),
  password: Yup.string().test({
    name: "conditional-validation",
    exclusive: false,
    params: {},
    message:
      "Password must be at least 8 characters and include letters, numbers, and at least one special character",
    test: function (value) {
      const { currentPassword, confirmPassword } = this.parent;
      // If any of the fields are filled, enforce password rules
      if (!value && !currentPassword && !confirmPassword) return true;
      return Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
          /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?`~])[a-zA-Z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]+$/,
          "Password must include letters, numbers, and at least one special character"
        )
        .isValidSync(value);
    },
  }),
  confirmPassword: Yup.string().test({
    name: "conditional-validation",
    exclusive: false,
    params: {},
    message: "Passwords must match",
    test: function (value) {
      const { currentPassword, password } = this.parent;
      // If any of the fields are filled, confirmPassword must match password
      if (!value && !currentPassword && !password) return true;

      return value === password;
    },
  }),
});

export const agentProfileSchema = Yup.object().shape({
  company: Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Company name is required")
      .min(1, "Name must be at least 1 character")
      .max(100, "Name must not exceed 100 characters"),
    phoneNumber: Yup.string().required("Phone number is required"),
    licenseNumber: Yup.string().trim().required("Licence number is required"),
    websiteUrl: Yup.string()
      .trim()
      .required("Website URL is required")
      .max(500, "URL must not exceed 500 characters")
      .test("is-valid-url", "Must be a valid URL", (value) => {
        if (!value) return false; // Required field
        const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*$/;
        return urlPattern.test(value);
      }),
    addressLine1: Yup.string()
      .trim()
      .required("Office address is required")
      .max(200, "Address must not exceed 200 characters"),
    addressLine2: Yup.string()
      .trim()
      .nullable()
      .max(200, "Address Line 2 should not exceed 200 characters"),
    city: Yup.string().trim().required("City is required"),
    state: Yup.string().trim().required("State/Province is required"),
    countryId: Yup.string().required("Country is required"),
    officeHours: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Office hour name is required"),
          isActive: Yup.boolean().required("Active status is required"),
          startTime: Yup.string().required("Start time is required"),
          endTime: Yup.string().required("End time is required"),
          startTimeError: Yup.string()
            .nullable()
            .oneOf([null], "Start time error must be null"),
          endTimeError: Yup.string()
            .nullable()
            .oneOf([null], "End time error must be null"),
        })
      )
      .min(7, "Valid office hour is required")
      .test("at-least-one-active", "At least one day must be active", (value) =>
        value?.some((item) => item.isActive)
      ),
  }),
  agent: Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("First name is required")
      .max(100, "First name must not 100 characters"),
    lastName: Yup.string()
      .trim()
      .required("Last name is required")
      .max(100, "Last name must not 100 characters"),
    phoneNumber: Yup.string().required("Phone number is required"),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must not exceed 100 characters")
      .test(
        "is-valid-email",
        "Invalid email address",
        (value) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ),
    languageSpokenIds: Yup.array()
      .min(1, "At least one language is required")
      .required("Languages spoken is required"),
    preferredContactMethod: Yup.string().required(
      "Preferred contact method is required"
    ),
    currentPassword: Yup.string().test({
      name: "conditional-validation",
      exclusive: false,
      params: {},
      message: "Old Password is required",
      test: function (value) {
        const { password, confirmPassword } = this.parent;
        // If any of the fields are filled, currentPassword must be required
        return !value && !password && !confirmPassword ? true : !!value;
      },
    }),
    password: Yup.string().test({
      name: "conditional-validation",
      exclusive: false,
      params: {},
      message:
        "Password must be at least 8 characters and include letters, numbers, and at least one special character",
      test: function (value) {
        const { currentPassword, confirmPassword } = this.parent;
        // If any of the fields are filled, enforce password rules
        if (!value && !currentPassword && !confirmPassword) return true;
        return Yup.string()
          .required("Password is required")
          .min(8, "Password must be at least 8 characters")
          .matches(
            /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?`~])[a-zA-Z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]+$/,
            "Password must include letters, numbers, and at least one special character"
          )
          .isValidSync(value);
      },
    }),
    confirmPassword: Yup.string().test({
      name: "conditional-validation",
      exclusive: false,
      params: {},
      message: "Passwords must match",
      test: function (value) {
        const { currentPassword, password } = this.parent;
        // If any of the fields are filled, confirmPassword must match password
        if (!value && !currentPassword && !password) return true;

        return value === password;
      },
    }),
    emergencyContactInfo: Yup.object().shape({
      fullName: Yup.string()
        .trim()
        .nullable()
        .max(100, "Full name must not exceed 100 characters"),
      phoneNumber: Yup.string().nullable(),
    }),
  }),
  files: Yup.array().nullable(),
  headshotfiles: Yup.array().nullable(),
  document: Yup.array().min(1, "Registration certificate is required"),
});

export const feedbackSchema = Yup.object().shape({
  userFeedback: Yup.string()
    .trim()
    .required("User feedback is required")
    .max(2000, "Feedback must not exceed 2000 characters"),
});
